import ClientPortalAPIService from './../services/api/ClientPortal/ApiService'
import MapBox from './map'
import { MyFleetPopupComponent } from '../components/MyFleetPopup/MyFleetPopup'
import Airport from './../modules/airport'
import * as turf from '@turf/turf'
import moment from 'moment'
import 'moment-timezone'
import { getStandarizedCoordinates } from '../services/MapService'

export class TailsModule {
    showTails = window.sessionStorage.getItem('showMapTails') === 'true' ? true : false
    trackedTailsByUser = []
    tails = null
    moduleInitialized = false
    isApiCallInProgress = false
    isWaypointsFetchInProgress = false
    lastWaypointsFetchTime = null
    lastTailsFetchTime = null
    MIN_WAYPOINTS_FETCH_INTERVAL = 5000 // 5 seconds minimum between waypoint fetches
    MIN_TAILS_FETCH_INTERVAL = 5000 // 5 seconds minimum between tails data fetches
    initialize() {
        this.getUserFlightTrackingInfo(true)
        this.startTailsObserver()
    }

    // This function is used externally to let know that some changes were made to the tails
    update() {
        if (!this.moduleInitialized) {
            this.initialize()
        } else {
            if (!this.isApiCallInProgress) {
                this.getUserFlightTrackingInfo(false)
            }
        }
    }

    /**
     * Predicts aircraft position using dead reckoning: extrapolates from last known
     * position, heading, and ground speed over the elapsed time.
     * @param {Object} lastPosition - Last known state: { latLng: [lat, lng], heading: degrees 0-360, speed: knots }
     * @param {number} timeSinceLastPositionReceived - Elapsed time in seconds (or ms if value > 1000)
     * @returns {[number, number]|null} Predicted [longitude, latitude] or null if prediction not possible
     */
    getNewPredictedPosition = (lastPosition, timeSinceLastPositionReceived) => {
        if (!lastPosition?.latLng || !Array.isArray(lastPosition.latLng) || lastPosition.latLng.length < 2) {
            return null
        }

        const [lat, lng] = lastPosition.latLng
        const heading = lastPosition.heading != null ? Number(lastPosition.heading) : null
        const speedKnots = lastPosition.speed != null ? Number(lastPosition.speed) : 0

        if (heading == null || speedKnots <= 0 || timeSinceLastPositionReceived <= 0) {
            return null
        }

        // Support both seconds and milliseconds (caller may pass ms from Date.now() - timestamp)
        const seconds = timeSinceLastPositionReceived > 1000
            ? timeSinceLastPositionReceived / 1000
            : timeSinceLastPositionReceived

        // Dead reckoning: distance = speed × time. Speed in knots = nautical miles per hour.
        const hours = seconds / 3600
        const distanceNauticalMiles = speedKnots * hours

        const from = turf.point([lng, lat])
        const destination = turf.destination(from, distanceNauticalMiles, heading, { units: 'nauticalmiles' })
        return destination.geometry.coordinates
    }

    fetchWaypoints = (updateDisplayCallback) => {
        if (!this.tails || this.isWaypointsFetchInProgress) {
            return Promise.resolve()
        }

        // Get waypoints for flying aircraft
        const flyingTails = this.tails.filter(tail => tail.onGround === false)
        const tailNumbers = flyingTails.map(tail => tail.tailNumber).filter(tailNumber => tailNumber)
        
        if (tailNumbers.length === 0) {
            return Promise.resolve()
        }

        this.isWaypointsFetchInProgress = true
        this.lastWaypointsFetchTime = Date.now()

        return ClientPortalAPIService.getWaypoints({ tails: tailNumbers })
            .then(waypointsResponse => {
                // Create a map of tailNumber to waypoints for quick lookup
                const waypointsMap = new Map()
                waypointsResponse.forEach(item => {
                    if (item.tailNumber && item.waypoints) {
                        waypointsMap.set(item.tailNumber, item.waypoints)
                    }
                })
                
                // Attach waypoints to corresponding tails
                this.tails.forEach(tail => {                     
                    if (waypointsMap.has(tail.tailNumber)) {
                        const currentPosition = [tail.latLng[1], tail.latLng[0]]
                        // When we call the first API it returns as the list of tails and it contains tail.LatLng to indicate the current position
                        // Then we call the waypoints API and because of a small delay, sometimes there are few waypoints ahead of the current position
                        // which results in a tiny line ahead of the current position. To avoid this, check that the waypoints array contains the
                        // current position and if it does, then remove those potential few waypoints ahead of it.
                        const waypoints = waypointsMap.get(tail.tailNumber)
                        // First find the index of the current position in the waypoints array
                        const currentPositionIndex = waypoints.findIndex(waypoint => waypoint[0] === currentPosition[0] && waypoint[1] === currentPosition[1])
                        if (currentPositionIndex > 0) {
                            // Then remove the waypoints ahead of the current position
                            waypoints.splice(0, currentPositionIndex)
                        }


                        // The BE doesn't return ESTI positions anymore, so this could produce a tiny gap between tail.LatLng which could be
                        // an ESTI and missing that from waypoints, resulting in a tiny gap. Just to be sure add always that point to the waypoints.
                        if (currentPosition) {
                            waypointsMap.get(tail.tailNumber).unshift(currentPosition)
                        }
                        
                        const messageTime = tail?.messageTime
                        let timePassedSinceLastPositionReceived = null
                        if (messageTime) {
                            const utcMessageTime = typeof messageTime === 'string' && !messageTime.endsWith('Z')
                                ? messageTime + 'Z'
                                : messageTime
                            const messageTimeMs = moment.utc(utcMessageTime).valueOf()
                            if (!isNaN(messageTimeMs)) {
                                timePassedSinceLastPositionReceived = Date.now() - messageTimeMs
                            }
                        }

                        const MIN_STALE_THRESHOLD_MS = 2 * 60 * 1000 // 2 minutes
                        let predictedPosition = null
                        if (timePassedSinceLastPositionReceived != null && timePassedSinceLastPositionReceived >= MIN_STALE_THRESHOLD_MS) {
                            let heading = tail.heading ? parseInt(tail.heading) : 0

                            if (tail.arrivalAirportIcao !== undefined && tail.arrivalAirportIcao !== null) {
                                const aiportOfDestination = Airport.getAirportByICAOCode(tail.arrivalAirportIcao)
                                if (aiportOfDestination !== undefined && aiportOfDestination !== null && aiportOfDestination.Lng !== undefined && aiportOfDestination.Lng !== null && aiportOfDestination.Lat !== undefined && aiportOfDestination.Lat !== null) {
                                    const distanceToDestination = turf.distance(turf.point([tail.latLng[1], tail.latLng[0]]), turf.point([aiportOfDestination.Lng, aiportOfDestination.Lat]), { units: 'kilometers' })

                                    const MAX_DISTANCE_TO_PREDICT_HEADING = 100
                                    if (distanceToDestination < MAX_DISTANCE_TO_PREDICT_HEADING) {
                                        // If the distance is less than MAX_DISTANCE_TO_PREDICT_HEADING km and we need to predict the heading, then don't use 
                                        // the heading from the tail but find new heading based on the destination airport
                                        const headingToDestination = turf.bearing(turf.point([tail.latLng[1], tail.latLng[0]]), turf.point([aiportOfDestination.Lng, aiportOfDestination.Lat]), { units: 'degrees' })
                                        heading = headingToDestination
                                        console.log(`We predicted a new heading for tail number ${tail.tailNumber} because the distance to the destination is less than ${MAX_DISTANCE_TO_PREDICT_HEADING} km`)
                                    }
                                }
                            }
                            const lastPosition = {
                                heading: heading,
                                latLng: tail.latLng,
                                speed: tail.speed
                            }
                            predictedPosition = this.getNewPredictedPosition(lastPosition, timePassedSinceLastPositionReceived)
                            const minutesAgo = (timePassedSinceLastPositionReceived / 60000).toFixed(1)
                            console.log(`We predicted a new position for tail number ${tail.tailNumber} because the last position was received ${minutesAgo} minutes ago`)
                        }

                        // If we needed to predict a new position (because SPIRE didn't return one), add it to the waypoints and also update the tail's latLng
                        // which is used for placing the icon if the aircraft on the map
                        if (predictedPosition) {
                            tail.latLng = [predictedPosition[1], predictedPosition[0]]
                            waypointsMap.get(tail.tailNumber).unshift(predictedPosition)
                        }

                        tail.waypoints = waypointsMap.get(tail.tailNumber)
                    }
                })
                
                if (updateDisplayCallback) {
                    updateDisplayCallback()
                }
            })
            .catch(error => {
                console.error('Error getting waypoints', error)
                if (updateDisplayCallback) {
                    updateDisplayCallback()
                }
            })
            .finally(() => {
                this.isWaypointsFetchInProgress = false
            })
    }

    fetchWaypointsIfNeeded = (updateDisplayCallback) => {
        // Check if we should fetch waypoints
        const now = Date.now()
        const timeSinceLastFetch = this.lastWaypointsFetchTime ? now - this.lastWaypointsFetchTime : Infinity
        
        // Don't fetch if:
        // 1. Already fetching
        // 2. Fetched recently (within minimum interval)
        // 3. No tails data available
        if (this.isWaypointsFetchInProgress || 
            timeSinceLastFetch < this.MIN_WAYPOINTS_FETCH_INTERVAL ||
            !this.tails) {
            return Promise.resolve()
        }

        // Check if waypoints are missing for flying aircraft
        const flyingTails = this.tails.filter(tail => tail.onGround === false)
        const needsWaypoints = flyingTails.some(tail => !tail.waypoints || tail.waypoints.length === 0)
        
        if (needsWaypoints) {
            return this.fetchWaypoints(updateDisplayCallback)
        }

        return Promise.resolve()
    }

    getUserFlightTrackingInfo = (isFirstTime, skipWaypoints = false) => {
        this.isApiCallInProgress = true
        this.lastTailsFetchTime = Date.now()
        ClientPortalAPIService.getUserFlightTrackingInfo().then(userFlightTrackingInfo => {
            userFlightTrackingInfo.forEach(flight => {
                // Well now is where the fun part starts. Spire data, but in general aicrafts often turn off their ADS-B transponders which results
                // sometimes in cases where aicraft has a low altitude and speed, well we've seen cases where transponders were turned off at high
                // altitude just when starting descending approach. Since they don't transmit data, we won't get a positon message that indicates
                // that the aircraft is on the ground and it will take a bit of time before the cron job for deleting data will kick in. So we need to
                // have a bit of a logic that tries to calculate if the aircraft has landed and update it accordingly.
                // TODO: Develop the logic, probably using combination of below parameters:
                // arrivalAirportIcao, arrivalScheduledTime, messageTime, alt, speed

                if (flight.onGround !== null && flight.onGround === false) {
                    // CASE 1: We have low speed and low altitude and we stop receiving messages, if the speed is under 300 and the altitude is under 500 ft
                    // then mark as on-ground: true. The values are a bit arbitrary, but it's a good starting point (would need more data to be
                    // more accurate). Also in this case we need to see that the last message was received more than 5 minutes ago.
                    const MIN_SPEED_FOR_ON_GROUND = 300
                    const MIN_ALTITUDE_FOR_ON_GROUND = 500
                    const MIN_TIME_SINCE_LAST_MESSAGE_FOR_ON_GROUND = 5 * 60 * 1000
                    let timeSinceLastMessage = null
                    if (flight.messageTime) {
                        const utcMessageTime = typeof flight.messageTime === 'string' && !flight.messageTime.endsWith('Z')
                            ? flight.messageTime + 'Z'
                            : flight.messageTime
                        const messageTimeMs = moment.utc(utcMessageTime).valueOf()
                        if (!isNaN(messageTimeMs)) {
                            timeSinceLastMessage = Date.now() - messageTimeMs
                        }
                    }
                    if (flight.speed < MIN_SPEED_FOR_ON_GROUND && flight.alt < MIN_ALTITUDE_FOR_ON_GROUND && timeSinceLastMessage != null && timeSinceLastMessage >= MIN_TIME_SINCE_LAST_MESSAGE_FOR_ON_GROUND) {
                        const timeSinceLastMessageReadable = moment.duration(timeSinceLastMessage).humanize()
                        console.log('----------------------------------------')
                        console.log(`We marked aircraft ${flight.tailNumber} as on-ground because the speed is under ${MIN_SPEED_FOR_ON_GROUND} and the altitude is under ${MIN_ALTITUDE_FOR_ON_GROUND} ft and the last message was received more than ${MIN_TIME_SINCE_LAST_MESSAGE_FOR_ON_GROUND / 60000} minutes ago.`)
                        console.log(`We are assuming that the aircraft has landed. Time since last message: ${timeSinceLastMessageReadable}, speed: ${flight.speed}, altitude: ${flight.alt}`)
                        flight.onGround = true
                        flight.status = 'on-ground'
                    }

                    // CASE 2: We have some cases where we don't have an estimated or schedule time and where an aicraft still has a considerable
                    // speed and altitude, an example we encountered had altitude: 19875 and speed: 387, indicating that it likely turned
                    // off its transponder quite early during descending approach. In this case we assume we have arrivalAirportIcao
                    // Let's start by making the first assumption that descent approach speed needs to be lower than 500 and that the distance to the
                    // airport of destination is less than 300km, and we haven't received any messages in that last 20 minutes, if all of these 
                    // conditions are met, then mark as on-ground: true.
                    const MIN_SPEED_FOR_DESCENT_APPROACH = 500
                    const MAX_DISTANCE_TO_DESCENT_APPROACH = 300
                    const MIN_TIME_SINCE_LAST_MESSAGE_FOR_DESCENT_APPROACH = 20 * 60 * 1000

                    let distanceToArrivalAirportKm = null
                    if (flight.latLng && Array.isArray(flight.latLng) && flight.latLng.length >= 2 && flight.arrivalAirportIcao) {
                        const arrivalAirport = Airport.getAirportByICAOCode(flight.arrivalAirportIcao)
                        if (arrivalAirport?.Lat != null && arrivalAirport?.Lng != null) {
                            distanceToArrivalAirportKm = turf.distance(
                                turf.point([flight.latLng[1], flight.latLng[0]]),
                                turf.point([arrivalAirport.Lng, arrivalAirport.Lat]),
                                { units: 'kilometers' }
                            )
                        }
                    }

                    if (flight.speed < MIN_SPEED_FOR_DESCENT_APPROACH && distanceToArrivalAirportKm != null && distanceToArrivalAirportKm < MAX_DISTANCE_TO_DESCENT_APPROACH && timeSinceLastMessage != null && timeSinceLastMessage >= MIN_TIME_SINCE_LAST_MESSAGE_FOR_DESCENT_APPROACH) {
                        const timeSinceLastMessageReadable = moment.duration(timeSinceLastMessage).humanize()
                        console.log('----------------------------------------')
                        console.log(`We marked aircraft ${flight.tailNumber} as on-ground because the speed is under ${MIN_SPEED_FOR_DESCENT_APPROACH} and the distance to the destination is less than ${MAX_DISTANCE_TO_DESCENT_APPROACH} km and the last message was received more than ${MIN_TIME_SINCE_LAST_MESSAGE_FOR_DESCENT_APPROACH / 60000} minutes ago.`)
                        console.log(`We are assuming that the aircraft has landed. Time since last message: ${timeSinceLastMessageReadable}, speed: ${flight.speed}, distance to destination: ${Math.round(distanceToArrivalAirportKm)} km`)
                        flight.onGround = true
                        flight.status = 'on-ground'
                    }

                    // CASE 3: There are Position-only Flight that don't have any airport of origin or destination and don't have an estimated or schedule time
                    // We saw a case where they stopped transmitting data with alt: 14000 and speed: 266.7, there is very little we can do in this case. The best we can do for now
                    // Is to regonize these Position-only Flight for low speed aicrafts (below 400 and low altitude below 18000 ft), so if we don't receive any
                    // messages in the last 20 minutes, then mark as on-ground: true.
                    const MIN_SPEED_FOR_POSITION_ONLY_FLIGHT = 400
                    const MIN_ALTITUDE_FOR_POSITION_ONLY_FLIGHT = 18000
                    const MIN_TIME_SINCE_LAST_MESSAGE_FOR_POSITION_ONLY_FLIGHT = 20 * 60 * 1000
                    if (flight.departureAirportIcao === null && 
                        flight.arrivalAirportIcao === null && 
                        flight.arrivalEstimatedTime === null && 
                        flight.arrivalScheduledTime === null && 
                        flight.speed < MIN_SPEED_FOR_POSITION_ONLY_FLIGHT && 
                        flight.alt < MIN_ALTITUDE_FOR_POSITION_ONLY_FLIGHT && 
                        timeSinceLastMessage != null && 
                        timeSinceLastMessage >= MIN_TIME_SINCE_LAST_MESSAGE_FOR_POSITION_ONLY_FLIGHT) {
                        const timeSinceLastMessageReadable = moment.duration(timeSinceLastMessage).humanize()
                        console.log('----------------------------------------')
                        console.log(`We marked aircraft ${flight.tailNumber} as on-ground because the speed is under ${MIN_SPEED_FOR_POSITION_ONLY_FLIGHT} and the altitude is under ${MIN_ALTITUDE_FOR_POSITION_ONLY_FLIGHT} ft and the last message was received more than ${MIN_TIME_SINCE_LAST_MESSAGE_FOR_POSITION_ONLY_FLIGHT / 60000} minutes ago.`)
                        console.log(`We are assuming that the aircraft has landed. Time since last message: ${timeSinceLastMessageReadable}, speed: ${flight.speed}, altitude: ${flight.alt}`)
                        flight.onGround = true
                        flight.status = 'on-ground'
                    }

                    // CASE 4: Aircrafts with high altitude and high speed that stop transmitting data and which have the airport of destination specified
                    // We encountered some cases like this, for example aircraft not submitting any data with altitude: 35000 and speed: 444.3 knots
                    // If we don't get any data for more than 20 minutes (Short/efficient descent into a quiet airport time) then only at that moment check
                    // the distance between last known position and the airport of destination, then based on the speed calculate the time it would
                    // theorically take to reach if it maintained that speed (add 25 minutes on top of that, because the planes slow down during descent)
                    // Then if the time since last message is greater than or equal to the estimated time to reach, mark as on-ground: true.
                    const MIN_SPEED_FOR_HIGH_ALT_SPEED_CASE = 400
                    const MIN_ALTITUDE_FOR_HIGH_ALT_SPEED_CASE = 18000
                    const MIN_TIME_SINCE_LAST_MESSAGE_FOR_HIGH_ALT_SPEED = 20 * 60 * 1000
                    const DESCENT_BUFFER_MINUTES = 25

                    if (flight.arrivalAirportIcao &&
                        flight.speed >= MIN_SPEED_FOR_HIGH_ALT_SPEED_CASE &&
                        flight.alt >= MIN_ALTITUDE_FOR_HIGH_ALT_SPEED_CASE &&
                        timeSinceLastMessage != null &&
                        timeSinceLastMessage >= MIN_TIME_SINCE_LAST_MESSAGE_FOR_HIGH_ALT_SPEED) {
                        const arrivalAirport = Airport.getAirportByICAOCode(flight.arrivalAirportIcao)
                        if (arrivalAirport?.Lat != null && arrivalAirport?.Lng != null && flight.latLng && Array.isArray(flight.latLng) && flight.latLng.length >= 2) {
                            const distanceToDestinationNm = turf.distance(
                                turf.point([flight.latLng[1], flight.latLng[0]]),
                                turf.point([arrivalAirport.Lng, arrivalAirport.Lat]),
                                { units: 'nauticalmiles' }
                            )
                            // Time in minutes = (distance in nm / speed in knots) * 60 + descent buffer
                            const timeToReachAtCruiseSpeedMinutes = (distanceToDestinationNm / flight.speed) * 60
                            const estimatedTimeToReachMinutes = timeToReachAtCruiseSpeedMinutes + DESCENT_BUFFER_MINUTES
                            const timeSinceLastMessageMinutes = timeSinceLastMessage / 60000

                            if (timeSinceLastMessageMinutes >= estimatedTimeToReachMinutes) {
                                const timeSinceLastMessageReadable = moment.duration(timeSinceLastMessage).humanize()
                                console.log('----------------------------------------')
                                console.log(`We marked aircraft ${flight.tailNumber} as on-ground (CASE 4: high alt/speed, no data).`)
                                console.log(`Time since last message: ${timeSinceLastMessageReadable} (${timeSinceLastMessageMinutes.toFixed(0)} min) >= estimated time to reach: ${estimatedTimeToReachMinutes.toFixed(0)} min. Last known: alt ${flight.alt} ft, speed ${flight.speed} knots, distance ${Math.round(distanceToDestinationNm)} nm`)
                                flight.onGround = true
                                flight.status = 'on-ground'
                            }
                        }
                    }

                    // CASE 5: Just a safeguard in case server still returns a flight like that, so if we don't receive any data for more than 16 hours, then mark as on-ground: true.
                    // This seems like a reasonable assumption, because it's very unlikely that an aircraft would be flying for 16 hours without submitting any data.
                    const MIN_TIME_SINCE_LAST_MESSAGE_FOR_SAFEGUARD = 16 * 60 * 60 * 1000
                    if (timeSinceLastMessage != null && timeSinceLastMessage >= MIN_TIME_SINCE_LAST_MESSAGE_FOR_SAFEGUARD) {
                        const timeSinceLastMessageReadable = moment.duration(timeSinceLastMessage).humanize()
                        console.log('----------------------------------------')
                        console.log(`We marked aircraft ${flight.tailNumber} as on-ground because the last message was received more than ${MIN_TIME_SINCE_LAST_MESSAGE_FOR_SAFEGUARD / 60000} hours ago. Case 5: Safeguard`)
                        console.log(`We are assuming that the aircraft has landed. Time since last message: ${timeSinceLastMessageReadable}`)
                        flight.onGround = true
                        flight.status = 'on-ground'
                    }
                }

                // Calculate distance between the two airports
                let directDistanceKilometers = 0
                let directDistanceMiles = 0
                if (flight.arrivalAirportIcao !== undefined && flight.arrivalAirportIcao !== null) {
                    const airportOfOriginDetails = Airport.getAirportByICAOCode(flight.departureAirportIcao)
                    const airportOfDestinationDetails = Airport.getAirportByICAOCode(flight.arrivalAirportIcao)

                    flight.departureAirportIata = airportOfOriginDetails?.IATA
                    flight.arrivalAirportIata = airportOfDestinationDetails?.IATA

                    if (airportOfOriginDetails !== undefined && airportOfDestinationDetails !== undefined) {
                        const from = turf.point([airportOfOriginDetails.Lng, airportOfOriginDetails.Lat])
                        const to = turf.point([airportOfDestinationDetails.Lng, airportOfDestinationDetails.Lat])

                        directDistanceKilometers = Math.round(turf.distance(from, to, { units: 'kilometers' }))
                        directDistanceMiles = Math.round(turf.distance(from, to, { units: 'miles' }))
                    }
                }
                flight.directDistanceInKilometers = directDistanceKilometers
                flight.directDistanceInMiles = directDistanceMiles
            })

            this.tails = userFlightTrackingInfo || []
            
            // Don't grab extra waypoints if the user is not displaying the tails on the map or if skipWaypoints is true
            const showMapTails = window.sessionStorage.getItem('showMapTails') === 'true' ? true : false
            if (showMapTails && !skipWaypoints) {
                this.fetchWaypoints(() => {
                    isFirstTime ? this.display() : this.updateDisplay()
                })
            } else {
                isFirstTime ? this.display() : this.updateDisplay()
            }
        }).catch(error => {
            console.error('Error getting user flight tracking info', error)
        }).finally(() => {
            if (!this.moduleInitialized) {
                this.addEventsListeners()
                this.moduleInitialized = true
            }
            this.isApiCallInProgress = false 
        })
    }

    refreshTailsDataIfNeeded = () => {
        // Check if we should refresh tails data
        const now = Date.now()
        const timeSinceLastFetch = this.lastTailsFetchTime ? now - this.lastTailsFetchTime : Infinity
        
        // Don't fetch if:
        // 1. Already fetching
        // 2. Fetched recently (within minimum interval)
        if (this.isApiCallInProgress || timeSinceLastFetch < this.MIN_TAILS_FETCH_INTERVAL) {
            return Promise.resolve()
        }

        // Refresh tails data without fetching waypoints (we'll do that separately)
        return new Promise((resolve) => {
            // Call getUserFlightTrackingInfo with skipWaypoints=true
            this.getUserFlightTrackingInfo(false, true)
            
            // Wait for the API call to complete
            // Check immediately first in case it completes synchronously
            const checkCompletion = () => {
                if (!this.isApiCallInProgress) {
                    resolve()
                } else {
                    // If still in progress, check periodically
                    const checkInterval = setInterval(() => {
                        if (!this.isApiCallInProgress) {
                            clearInterval(checkInterval)
                            resolve()
                        }
                    }, 100)
                }
            }
            
            // Small delay to allow the API call to start
            setTimeout(checkCompletion, 10)
        })
    }

    async getModuleTails() {
        const tails = await new Promise((resolve) => {
            const observerInterval = window.setInterval(() => {
                if (this.tails !== null && this.moduleInitialized) {
                    resolve(this.tails)
                    window.clearInterval(observerInterval)
                }
            }, 25)
        })

        return tails
    }

    startTailsObserver() {
        const SECONDS_30 = 1000 * 30
        window.setInterval(() => {
            if (!this.isApiCallInProgress) {
                this.getUserFlightTrackingInfo(false)
            }
        }, SECONDS_30)
    }

    getTailsFeatureCollection(tails) {
        const featureCollection = {
            type: 'FeatureCollection',
            features: []
        }

        const guessedTimeZone = moment.tz(moment.tz.guess()).format('z')

        for (const tail of tails) {
            // We want to display only aircrafts that are in the air
            if (tail?.status === 'en-route' && tail?.onGround !== null && tail?.onGround === false) {
                // Skip if position is missing or invalid
                const position = tail?.latLng
                if (!position || !Array.isArray(position) || position.length < 2) {
                    continue
                }

                let landingTime = ''
                let scheduledTime = ''

                if (tail?.arrivalEstimatedTime) {
                    landingTime = moment(tail.arrivalEstimatedTime + 'Z').format('HH:mm') + ' ' + guessedTimeZone
                }

                if (tail?.arrivalScheduledTime) {
                    scheduledTime = moment(tail.arrivalScheduledTime + 'Z').format('HH:mm') + ' ' + guessedTimeZone
                }

                const properties = {
                    ['latitude']: position[0],
                    ['longitude']: position[1],
                    ['origin']: tail?.departureAirportIcao || '',
                    ['originIata']: tail?.departureAirportIata || '',
                    ['destination']: tail?.arrivalAirportIcao || '',
                    ['destinationIata']: tail?.arrivalAirportIata || '',
                    ['heading']: tail?.heading ? parseInt(tail.heading) : 0,
                    ['identifier']: tail?.tailNumber || '',
                    ['aircraftName']: tail?.aircraftTypeName || '',
                    ['landingTime']: landingTime,
                    ['scheduledTime']: scheduledTime,
                    // Most recent groundspeed (knots)
                    ['groundSpeedKilometers']: tail?.speed ? Math.round(tail.speed * 1.85200) : 0,
                    ['groundSpeedMiles']: tail?.speed ? Math.round(tail.speed * 1.15078) : 0,
                    // Aircraft altitude in hundreds of feet
                    ['altitudeMeters']: tail?.alt ? Math.round(tail.alt / 3.2808) : 0,
                    ['altitudeFeet']: tail?.alt ? Math.round(tail.alt) : 0,
                    ['directDistanceInKilometers']: tail?.directDistanceInKilometers || 0,
                    ['directDistanceInMiles']: tail?.directDistanceInMiles || 0
                }

                const point = {
                    type: 'Point',
                    coordinates: [position[1], position[0]]
                }

                const feature = {
                    type: 'Feature',
                    id: tail?.tailNumber || null,
                    geometry: point,
                    properties: properties
                }

                featureCollection.features.push(feature)
            }
        }

        return featureCollection
    }

    getTailsRoutesFutureMultiLineString(tails) {
        const feature = {
            type: 'Feature',
            properties: {},
            geometry: {
                type: 'MultiLineString',
                coordinates: []
            }
        }

        for (const tail of tails) {
            // We want to display only aircrafts that are in the air
            if (tail.status === 'en-route') {
                if (tail.waypoints?.length > 0) {
                    const waypointCoordinates = tail.waypoints
                    const arrayOfCoordinates = getStandarizedCoordinates(waypointCoordinates)
                    feature.geometry.coordinates.push(arrayOfCoordinates)
                }
            }
        }
        return feature
    }

    ensureMapReady(callback) {
        if (MapBox.map.isStyleLoaded()) {
            callback()
        } else {
            MapBox.map.once('load', callback)
        }
    }

    display() {
        const tailsFeatureCollection = this.getTailsFeatureCollection(this.tails)
        const tailsRoutesFeatureMultiLineString = this.getTailsRoutesFutureMultiLineString(this.tails)

        this.ensureMapReady(() => {
            // Check Avsec module for details about this timeout
            MapBox.map.addSource('tails-waypoints', {
                'type': 'geojson',
                'lineMetrics': true,
                'data': tailsRoutesFeatureMultiLineString
            })

            MapBox.map.addLayer({
                'id': 'tails-waypoints',
                'type': 'line',
                'source': 'tails-waypoints',
                'layout': {
                    'visibility': this.showTails ? 'visible' : 'none',
                    'line-join': 'round',
                    'line-cap': 'round'
                },
                'paint': {
                    'line-color': '#0085FF',
                    'line-width': 2,
                    'line-gradient': [
                        'interpolate',
                        ['linear'],
                        ['line-progress'],
                        0, 'rgba(0, 133, 255, 0.2)',
                        0.2, 'rgba(0, 133, 255, 1)',
                        0.55, 'rgba(0, 133, 255, 0.2)',
                        0.7, 'rgba(0, 133, 255, 1)',
                        1, 'rgba(0, 133, 255, 0.2)'
                    ]
                }
            })

            MapBox.map.addSource('tails', {
                'type': 'geojson',
                'data': tailsFeatureCollection
            })

            MapBox.map.addLayer({
                id: 'tails',
                type: 'symbol',
                source: 'tails',
                layout: {
                    'visibility': this.showTails ? 'visible' : 'none',
                    'icon-image': 'tail-marker',
                    'icon-rotate': ['get', 'heading'],
                    'icon-allow-overlap': true,
                    'text-allow-overlap': true,
                    'icon-size': ['interpolate', ['linear'], ['zoom'], 3.5, 0.2, 4.5, 0.3, 7, 0.4]
                }
            })
        })
    }

    updateDisplay() {
        const tailsFeatureCollection = this.getTailsFeatureCollection(this.tails)
        const tailsRoutesFeatureMultiLineString = this.getTailsRoutesFutureMultiLineString(this.tails)

        this.ensureMapReady(() => {
            const tailsSource = MapBox.map.getSource('tails')
            const tailsWaypointsSource = MapBox.map.getSource('tails-waypoints')

            if (!tailsSource && !tailsWaypointsSource) {
                this.display()
            } else {
                if (tailsSource) {
                    tailsSource.setData(tailsFeatureCollection)
                }
                if (tailsWaypointsSource) {
                    tailsWaypointsSource.setData(tailsRoutesFeatureMultiLineString)
                }
            }
        })
    }

    show() {
        this.ensureMapReady(() => {
            if (MapBox.map.getSource('tails') !== undefined) {
                MapBox.map.setLayoutProperty('tails', 'visibility', 'visible')
            }

            if (MapBox.map.getSource('tails-waypoints') !== undefined) {
                MapBox.map.setLayoutProperty('tails-waypoints', 'visibility', 'visible')
            }

            // First refresh tails data to ensure aircraft positions are up to date
            // Then fetch waypoints if needed when showing the layer
            this.refreshTailsDataIfNeeded().then(() => {
                this.fetchWaypointsIfNeeded(() => {
                    this.updateDisplay()
                })
            })
        })
    }

    hide() {
        this.ensureMapReady(() => {
            if (MapBox.map.getSource('tails') !== undefined) {
                MapBox.map.setLayoutProperty('tails', 'visibility', 'none')
            }

            if (MapBox.map.getSource('tails-waypoints') !== undefined) {
                MapBox.map.setLayoutProperty('tails-waypoints', 'visibility', 'none')
            }
        })
    }

    addEventsListeners() {
        MapBox.map.on('mouseenter', 'tails', (e) => {
            const tailData = e.features[0].properties

            MyFleetPopupComponent.$data.display = 'block'
            MyFleetPopupComponent.$data.identifier = tailData.identifier
            MyFleetPopupComponent.$data.aircraftName = tailData.aircraftName
            MyFleetPopupComponent.$data.origin = tailData.origin
            MyFleetPopupComponent.$data.destination = tailData.destination
            MyFleetPopupComponent.$data.originIATA = tailData.originIATA
            MyFleetPopupComponent.$data.destinationIATA = tailData.destinationIATA
            MyFleetPopupComponent.$data.landingTime = tailData.landingTime
            MyFleetPopupComponent.$data.scheduledTime = tailData.scheduledTime
            MyFleetPopupComponent.$data.groundSpeedKilometers = tailData.groundSpeedKilometers
            MyFleetPopupComponent.$data.groundSpeedMiles = tailData.groundSpeedMiles
            MyFleetPopupComponent.$data.altitudeMeters = tailData.altitudeMeters
            MyFleetPopupComponent.$data.altitudeFeet = tailData.altitudeFeet
            MyFleetPopupComponent.$data.directDistanceInKilometers = tailData.directDistanceInKilometers
            MyFleetPopupComponent.$data.directDistanceInMiles = tailData.directDistanceInMiles

            MapBox.map.getCanvas().style.cursor = 'pointer'
        })

        MapBox.map.on('mousemove', 'tails', (e) => {
            MyFleetPopupComponent.$data.leftPosition = e.point.x + 15 + 'px'
            MyFleetPopupComponent.$data.topPosition = e.point.y + 15 + 'px'
        })

        MapBox.map.on('mouseleave', 'tails', (e) => {
            MyFleetPopupComponent.$data.display = 'none'
            MapBox.map.getCanvas().style.cursor = 'inherit'
        })
    }
}

const Tails = new TailsModule()
export default Tails
